import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import styles from "./BulkSharing.module.scss";
import { pricingActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import { Dropdown, IItems } from "../dropdown/Dropdown";
import { Switch } from "../switch/Switch";
import BulkSharingItem from "./BulkSharingItem";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { ChooseRule } from "../chooseRule/ChooseRule";
import { ISavedRules } from "@src/store/pricing/savedRulesSlice";
import MarketplacesV2 from "../marketPlaces/MarketplacesV2";
import {
	isAnyListingInTMRestrictedCategory,
	isMarketplaceBlockedByTMConsent,
	syncLinkedDestinations,
	toggleDestinationSelection,
} from "../utils/marketplaceUtils";
import { ExportDestination } from "@src/constants/constants";

type TBulkSharing = {
	handleClose?: () => void;
};

type TState = {
	items: Array<EventInventoryRecord>;
	selectedExportDestinations: Array<number>;
	hideSeats: boolean;
	splits: number;
};

const BulkSharing = ({ handleClose }: TBulkSharing) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const { data: marketplaces } = useAppSelector((state) => state.system.exportDestinations);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);
	const { loading: shareListingsLoading } = useAppSelector((state) => state.pricing.listingsSharing);
	const { data: sharingSettings, loading: sharingSettingsLoading } = useAppSelector((state) => state.pricing.sharingSettings);
	const eventsList = useAppSelector((state) => state.pricing.events.data.Events);

	const inRestrictedCategory = isAnyListingInTMRestrictedCategory(items, eventsList);

	const [state, setState] = useState<TState>({
		items,
		selectedExportDestinations: [],
		hideSeats: false,
		splits: -1,
	});

	const handleExportDestinationToggle = (id: number) => {
		setState((prev) => ({
			...prev,
			selectedExportDestinations: toggleDestinationSelection(prev.selectedExportDestinations, id),
		}));
	};

	const handleShareEverywhere = () => {
		const marketplacesMap = marketplaces
			.filter((x) => !isMarketplaceBlockedByTMConsent(x, inRestrictedCategory))
			.filter((x) => hasCIMSMarketAccess || x.Export_Destination_ID !== ExportDestination.Cims)
			.map((x) => x.Export_Destination_ID);
		setState((prev) => ({ ...prev, selectedExportDestinations: marketplacesMap }));
	};

	const handleUnshareEverywhere = () => {
		setState((prev) => ({ ...prev, selectedExportDestinations: [] }));
	};

	const handleRemoveItem = (id: number) => {
		setState((prev) => ({ ...prev, items: prev.items.filter((x) => x.Listing_ID !== id) }));
	};

	const mappedItems = state.items.map((x) => {
		const markups = sharingSettings.fullMarkups
			.filter((y) => y.Listing_ID === x.Listing_ID)
			.map((x) => x.Export_Destination_ID);

		const sharedDestinations = marketplaces.filter((x) => markups.includes(x.Export_Destination_ID));

		return (
			<BulkSharingItem
				key={x.Item_ID}
				data={x}
				sharedDestinations={sharedDestinations}
				onRemove={(x) => handleRemoveItem(x)}
				loading={sharingSettingsLoading}
				disableTooltip={state.items.length > 25}
			/>
		);
	});

	const handleChangeSplits = (x: TState["splits"]) => {
		setState((prev) => ({ ...prev, splits: x }));
	};

	const splits: Array<IItems> = [
		{ id: 1, label: "Default", value: -1, onClick: () => handleChangeSplits(-1) },
		{ id: 2, label: "Pairs", value: 2, onClick: () => handleChangeSplits(2) },
		{ id: 3, label: "Any", value: 1, onClick: () => handleChangeSplits(1) },
		{ id: 4, label: "None", value: 0, onClick: () => handleChangeSplits(0) },
	];

	const buttonText = splits.find((x) => x.value === state.splits)?.label ?? "Default";

	const handleSubmit = async () => {
		if (!state.items.length) return;

		const ListingIds = state.items.map((x) => x.Listing_ID);
		const Visibility = state.hideSeats ? ("HIDE" as const) : ("SHOW" as const);

		const blockedDestinationIds = new Set(
			marketplaces.filter((m) => isMarketplaceBlockedByTMConsent(m, inRestrictedCategory)).map((m) => m.Export_Destination_ID),
		);

		const ShareOutSettings = syncLinkedDestinations(state.selectedExportDestinations)
			.filter((x) => !blockedDestinationIds.has(x))
			.filter((x) => hasCIMSMarketAccess || x !== ExportDestination.Cims)
			.map((x) => ({
				Export_Destination_ID: x,
				AllowedSplits: state.splits,
				Visibility,
			}));

		const payload = {
			ListingIds,
			ShareOutSettings,
		};

		const res = await dispatch(pricingActions.updateSharing(payload));

		if (res.meta.requestStatus === "fulfilled") {
			handleClose?.();
		}
	};

	const handleRulePick = (x: ISavedRules) => {
		setState((prev) => ({
			...prev,
			selectedExportDestinations: syncLinkedDestinations(x.Markups.map((x) => x.Export_Destination_ID)),
			splits: x.AllowedSplits,
			hideSeats: x.Visibility === "HIDE" ? true : false,
		}));
	};

	const handleRuleSave = (RuleName: string) => {
		const Markups = state.selectedExportDestinations.map((x) => ({ Export_Destination_ID: x }));
		const Visibility = state.hideSeats === true ? "HIDE" : "SHOW";
		const AllowedSplits = state.splits;

		const payload = {
			AllowedSplits,
			Visibility,
			Markups,
			RuleName,
		};

		dispatch(pricingActions.saveRule(payload));
	};

	useEffect(() => {
		const run = async () => {
			const res = await dispatch(pricingActions.getListingSharedSettings(items.map((x) => x.Listing_ID)));

			if (pricingActions.getListingSharedSettings.fulfilled.match(res)) {
				const first = res.payload[0];
				const selectedExportDestinations = syncLinkedDestinations([
					...new Set(res.payload.map((x) => x.Export_Destination_ID)),
				]);

				setState((prev) => ({
					...prev,
					selectedExportDestinations,
					splits: first ? first.AllowedSplits : prev.splits,
					hideSeats: first ? first.Visibility === "HIDE" : prev.hideSeats,
				}));
			}
		};

		run();
	}, [dispatch, items]);

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={state.items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, helperStyles.ellipsisDiv, styles.mb)}>Choose Rule</div>
					<div className={clx(styles.mb)}>
						<ChooseRule disableHeadline onRuleSave={handleRuleSave} onRulePick={handleRulePick} />
					</div>
					<div className={clx(styles.subHeader, helperStyles.ellipsisDiv, styles.mb)}>Listing Rules</div>
					<div className={styles.listingRulesContainer}>
						<Dropdown className={styles.dropdown} isDisabled={false} items={splits} buttonText={buttonText} />
						<Button variant={ButtonVariant.Secondary}>
							<Switch
								label="Hide Seats"
								checked={state.hideSeats}
								onChange={(e) => setState((prev) => ({ ...prev, hideSeats: e }))}
							/>
						</Button>
					</div>
					<div className={styles.broadcastHeader}>
						<div className={clx(styles.subHeader, helperStyles.ellipsisDiv)}>Broadcast Destinations</div>
						<div className={helperStyles.flexSmallGap}>
							{state.selectedExportDestinations.length ? (
								<div className={styles.shareSelect} onClick={handleUnshareEverywhere}>
									Unshare Everywhere
								</div>
							) : (
								<div className={styles.shareSelect} onClick={handleShareEverywhere}>
									Share Everywhere
								</div>
							)}
						</div>
					</div>
					<MarketplacesV2
						selectedExportDestinations={state.selectedExportDestinations}
						onMarkteplaceClick={handleExportDestinationToggle}
						inRestrictedCategory={inRestrictedCategory}
					/>
					{state.items.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.textSecondary}>
								Saving will overwrite any previous Scheduled Pricing settings saved to these listings.
							</div>
						</div>
					) : null}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Broadcast Destinations</div>
						<div className={styles.subHeaderSecondary}>Applying to {state.items.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={styles.secRowSeat}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.splitLabel}>Splits</div>
						<div className={styles.priceLabel}>Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					isLoading={shareListingsLoading}
					disabled={!state.items.length || shareListingsLoading}>
					Apply sharing
				</Button>
			</div>
		</div>
	);
};

export default BulkSharing;
